<template>
  <div class="inline-input-wrapper" :class="{'error': error}">
    <div class="inline-input" :class="{'inline-input--has-helptip': hasHelptip, 'inline-input--has-label': hasLabel}">
      <span v-if="label" class="inline-input-label">{{ label }}</span>
      <input :id="id" :name="name" :placeholder="placeholder" :type="type" :value="value" ref="input"
        @input="onInput($event.target.value)"
        @blur="onBlur()"
        @change="onBlur();$emit('change')"
        @focus="onfocus"
        @keyup="$emit('change')"
        :maxlength="maxInputCharacters()">
        <!-- emitting change is done in @keyup and @change, @keyup was added because IE11 doesn't support onchange events -->
      <tooltip-expanded-trigger v-if="hasHelptip" :expandedTipKey="helptipKey">
        <div class="inline-input-helptip">
          <i class="material-icons help" ttposition>help</i>
        </div>
      </tooltip-expanded-trigger>
      <i class="material-icons warning">warning</i>
    </div>
    <slot name="help-text"></slot>
    <slot name="errors"></slot>
  </div>
</template>

<script>
import { get as _get } from 'lodash';

// CardValidator
import cardValidator from 'card-validator';

// Components
import TooltipExpandedTrigger from '@/components/shared/tooltip-expanded-trigger';

// Service
import FormatService from '@/services/FormatService';

export default {
  name: 'inline-cc-input-field',
  props: {
    type: {
      type: String,
      required: true
    },
    value: {
      required: true
    },
    id: {
      type: String
    },
    name: {
      type: String
    },
    error: {
      type: Boolean
    },
    placeholder: {
      type: String
    },
    label: {
      type: String
    },
    helptipKey: {
      type: String
    },
    valueFormat: {
      type: String
    },
    maxCharacters: {
      type: Number
    },
    allowedCharacters: {
      type: RegExp
    }
  },
  components: {
    TooltipExpandedTrigger
  },
  computed: {
    hasHelptip() {
      return !!this.helptipKey;
    },
    hasLabel() {
      return !!this.label;
    },
    hasValueFormat() {
      return !!this.valueFormat;
    },
    hasAllowedCharacters() {
      return !!this.allowedCharacters;
    }
  },
  methods: {
    snakeToCamel(snakeCaseString) {
      var find = /(_\w)/g;
      var convert = function (matches) {
        return matches[1].toUpperCase();
      };
      var camelCaseString = snakeCaseString.replace(
        find,
        convert
      );
      return camelCaseString;
    },
    sanitizeInput(val) {
      return val.replace(this.allowedCharacters, '');
    },
    includeCreditCardIcon(value) {
      // Remove credit card icon classes before to set / update it
      this.$refs.input.className = '';
      let type = _get(cardValidator.number(value), 'card.type');
      if (value !== '') {
        if ((type === 'visa') || (type === 'master-card') || (type === 'discover')) {
          this.$refs.input.classList.add(type);
        }
      }
    },
    onInput(value) {
      // Sanitize input based on allowed characters
      let sanitizedValue;
      if (this.hasAllowedCharacters) {
        sanitizedValue = this.sanitizeInput(value);
      } else {
        sanitizedValue = value;
      }

      let result;
      let cursorPos = this.$refs.input.selectionStart;
      let numFollowingChars = sanitizedValue.length - cursorPos;

      if (this.hasValueFormat) {
        let camelCaseString = this.snakeToCamel(this.valueFormat);
        let parser = _get(FormatService, camelCaseString + '.parse');

        if (parser === undefined) {
          result = sanitizedValue;
        } else {
          result = parser(sanitizedValue);
        }
      } else {
        result = sanitizedValue;
      }
      this.includeCreditCardIcon(value);

      // Get formatted value
      let formattedValue = this.formatValue(result);

      let formattedFollowingChars = formattedValue.length - cursorPos;
      let newCursorPos = cursorPos + (formattedFollowingChars - numFollowingChars);

      this.$emit('input', result);
      this.$refs.input.value = formattedValue;

      this.$nextTick(() => {
        this.$refs.input.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
    formatValue(val) {
      if (val === undefined) {
        return ''; // undefined vals should return empty string so undefined isn't shown in UI
      } else if (this.hasValueFormat) {
        let camelCaseString = this.snakeToCamel(this.valueFormat);
        let formatter = _get(FormatService, camelCaseString + '.format');

        if (formatter !== undefined) {
          return formatter(val);
        }
      }

      return val;
    },
    maxInputCharacters() {
      if (this.maxCharacters) {
        if (this.valueFormat === 'number_with_commas') {
          const commaCount = Math.floor((this.maxCharacters - 1) / 3);
          return this.maxCharacters + commaCount;
        } else {
          return this.maxCharacters
        }
      }
    },
    onBlur() {
      let formatted = this.formatValue(this.value);
      this.$refs.input.value = formatted;
      this.$emit('blur');
    },
    onfocus(event) {
      this.$emit('focus');
    }
  },
  mounted() {
    let formatted = this.formatValue(this.$refs.input.value);
    this.$refs.input.value = formatted;
    this.includeCreditCardIcon(this.$refs.input.value);
  },
  updated() {
    let formatted = this.formatValue(this.value);
    this.$refs.input.value = formatted;
    this.includeCreditCardIcon(this.$refs.input.value);
  }
}
</script>

<style lang="scss">
// Override inline input field CSS properties
// These properties are being applied locally from sfi-foundation, but not in the deployed application
.inline-input-wrapper {
  .inline-input {
    // CSS properties copied from sfi-foundation
    // https://github.com/security-first-managers/sfi-foundation/blob/master/styles/inputs.scss#L7
    [type='text'],
    [type='password'],
    [type='date'],
    [type='datetime'],
    [type='datetime-local'],
    [type='month'],
    [type='week'],
    [type='email'],
    [type='number'],
    [type='search'],
    [type='tel'],
    [type='time'],
    [type='url'],
    [type='color'],
    textarea,
    select {
      font-weight: normal;
      min-width: 0px;
      padding-top: 0.375rem;
      padding-bottom: 0.375rem;
    }
  }
}

input.visa {
  background-image: url(../../assets/visa-card.svg);
}

input.master-card {
  background-image: url(../../assets/master-card.svg);
}

input.discover {
  background-image: url(../../assets/discover-card.svg);
}

input.visa,
input.master-card,
input.discover {
  background-position: 97% 50%;
  background-repeat: no-repeat;
  background-size: 9%;
}

.visa + .material-icons.warning,
.master-card + .material-icons.warning,
.discover + .material-icons.warning {
  margin-right: 35px;
}

</style>



// WEBPACK FOOTER //
// src/components/forms/inline-cc-input-field.vue